import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import rootStores from "../../stores";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import logo from "../../assets/logos/eventim_mc_neg.svg";

import {
  AppBar,
  Box,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Tooltip,
  MenuItem,
  Button,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import "./NavBar.css";
import { MenuItems } from "./constsMenu";
import { useTranslation } from "react-i18next";

interface IMenuItems {
  path: string;
  label: string; // translation key
  name: string;
  hasSubMenu: boolean;
  menuKey: number;
  subMenuKey?: number;
  isSubMenu?: boolean;
  permission?: string;
}

const authStore = rootStores.AuthStore;
const navBarStore = rootStores.NavBarStore;

const ResponsiveAppBar = observer(() => {
  const { t, i18n } = useTranslation();
  const { userName, logout, clubPrefix } = authStore;

  const [selected, setSelected] = useState<string>("");
  const [subMenuKey, setSubMenuKey] = useState<number>(0);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();

  // Determine current language
  const [isRTL, setIsRTL] = useState<boolean>(true);

  useEffect(() => {
    const savedLang = (localStorage.getItem("language") as "he" | "en") || "he";
    i18n.changeLanguage(savedLang).then(() => {
      const rtl = savedLang === "he";
      setIsRTL(rtl);
      document.body.dir = rtl ? "ltr" : "rtl";
    });
  }, [i18n]);

  const changeLanguage = (lng: "he" | "en") => {
    i18n.changeLanguage(lng).then(() => {
      localStorage.setItem("language", lng);
      const rtl = lng === "he";
      setIsRTL(rtl);
      document.body.dir = rtl ? "ltr" : "rtl";
    });
  };

  const handleClickComponent = (e: any, menuItem: IMenuItems) => {
    if (menuItem.hasSubMenu && menuItem.subMenuKey && subMenuKey === 0) {
      setSubMenuKey(menuItem.subMenuKey);
    } else if (subMenuKey !== 0 && !menuItem.isSubMenu) {
      setSubMenuKey(0);
    } else {
      navigate(menuItem.path);
      navBarStore.setSelectedTab(menuItem);
      setSelected(menuItem.label);
    }
  };

  const openSubMenu = (subMenuKey: number) => {
    const subMenu = MenuItems.filter((item) => item.menuKey === subMenuKey);
    return (
      <Container maxWidth={false}>
        {subMenu.map((item) => (
          <MenuItem
            key={item.name}
            onClick={(e) => handleClickComponent(e, item)}
            sx={{
              backgroundColor: getBackgroundColor(item),
              padding: "0 !important",
              "&:hover": { backgroundColor: getHoverBackgroundColor() },
            }}
          >
            <Link style={{ textDecoration: "none" }} to={item.path}>
              <Typography
                className="sub-menu-label"
                sx={{
                  display: "flex",
                  direction: isRTL ? "ltr" : "rtl",
                  textAlign: isRTL ? "left" : "right",
                  justifyContent: isRTL ? "flex-start" : "flex-end",
                  width: "100%",
                }}
              >
                {t(item.label)}
              </Typography>
            </Link>
          </MenuItem>
        ))}
      </Container>
    );
  };

  const getBackgroundColor = (page: IMenuItems) => {
    if (selected === page.label && !page.hasSubMenu) return "#FFFFFF";
    if (page.hasSubMenu && page.subMenuKey === subMenuKey) return "rgba(255,255,255,0.08)";
    return "#5C6BC0";
  };

  const getHoverBackgroundColor = () => "rgba(255,255,255,0.08)";

  return (
    <AppBar
      position="static"
      sx={{ width: "20%", backgroundColor: "#5C6BC0", maxWidth: 250 }}
    >
      <div className="app-bar-container">
        <Container maxWidth={false}>
          <img src={logo} alt="Logo" className="logo" />

          <div className="menu-container">
            {MenuItems.filter((item) => !item.isSubMenu && (!item.permission || item.permission === clubPrefix)).map((page) => (
              <MenuItem
                key={page.name}
                onClick={(e) => handleClickComponent(e, page)}
                sx={{
                  backgroundColor: getBackgroundColor(page),
                  "&:hover": { backgroundColor: getHoverBackgroundColor() },
                  justifyContent: isRTL ? "flex-end" : "flex-start",
                  flexDirection: "column",
                  alignItems: isRTL ? "flex-end" : "flex-start",
                }}
              >
                <div className="box-header">
                  <Typography
                    className="menu-label"
                    sx={{
                      color: selected === page.label ? "black" : "#FFFFFF",
                      direction: isRTL ? "ltr" : "rtl",
                      textAlign: isRTL ? "left" : "right",
                      justifyContent: isRTL ? "flex-end" : "flex-start",
                      width: "100%",
                    }}
                  >
                    {t(page.label)}
                  </Typography>
                  {page.hasSubMenu && <ArrowDropDownRoundedIcon />}
                </div>

                {page.hasSubMenu && page.subMenuKey === subMenuKey && (
                  <div className="sub-menu-container">{openSubMenu(subMenuKey)}</div>
                )}
              </MenuItem>
            ))}
          </div>

          {/* User section */}
          <Box
            className="user-details-container"
            sx={{
              flexDirection: isRTL ? "row-reverse" : "row",
              justifyContent: isRTL ? "flex-start" : "flex-end",
            }}
          >
            <Box onClick={(e) => setAnchorElUser(e.currentTarget)} sx={{ cursor: "pointer", display: "flex" }}>
              <Typography
                className="user-name-side-bar"
                sx={{ textAlign: isRTL ? "right" : "left", marginRight: isRTL ? 0 : 2 }}
              >
                {userName}
              </Typography>
              <Tooltip title={t("navbar.settings")}>
                <IconButton sx={{ p: 0 }}>
                  <Avatar sx={{ bgcolor: "white", color: blue[700] }}>
                    {userName?.slice(0, 1).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>

            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={() => setAnchorElUser(null)}
            >
              <MenuItem>
                <Button onClick={() => changeLanguage("he")}>עברית</Button>
              </MenuItem>
              <MenuItem>
                <Button onClick={() => changeLanguage("en")}>EN</Button>
              </MenuItem>
              <MenuItem>
                <Button onClick={logout}>{t("navbar.logout")}</Button>
              </MenuItem>
            </Menu>
          </Box>
        </Container>
      </div>
    </AppBar>
  );
});

export default ResponsiveAppBar;
